import React, { useEffect, useMemo, useState } from 'react';
import axios from 'axios';
import { AlertTriangle, CheckCircle2, Loader2, MessageCircle, ShieldCheck } from 'lucide-react';

import { useAuth } from '../../context/AuthContext';
import API_BASE from '../../utils/apiBase';
import { Badge } from '../ui/badge';
import { Button } from '../ui/button';

const API = API_BASE;

function domainSummary(domain) {
  return {
    outboundReady: domain?.outbound_ready === true,
    templateEnvName: domain?.template_env_name || null,
    configuredTemplateKeys: Array.isArray(domain?.template_summary?.configured_template_keys)
      ? domain.template_summary.configured_template_keys
      : [],
    configuredCount: Number(domain?.template_summary?.configured_count || 0),
  };
}

export function summarizeWhatsAppSetup(setup) {
  const checks = Array.isArray(setup?.checks) ? setup.checks : [];
  const allMissing = checks.filter((item) => item?.configured === false);
  const missing = allMissing.filter((item) => item?.key !== 'approved_templates');
  const optionalTemplateMissing = allMissing.filter((item) => item?.key === 'approved_templates');
  return {
    loaded: Boolean(setup),
    outboundReady: setup?.outbound_ready === true,
    manualSupportReady: setup?.manual_support_ready === true,
    webhookReady: setup?.webhook_ready === true,
    fullyConfigured: setup?.fully_configured === true,
    liveVerified: setup?.live_connectivity_verified === true,
    liveProbeAvailable: setup?.live_probe_available === true,
    callbackPath: setup?.callback_path || '/api/webhooks/meta/whatsapp',
    missing,
    optionalTemplateMissing,
    recruitment: domainSummary(setup?.domain_readiness?.recruitment),
    staff: domainSummary(setup?.domain_readiness?.staff),
  };
}


export function summarizeWhatsAppProbe(probe) {
  return {
    performed: probe?.live_probe_performed === true,
    verified: probe?.live_connectivity_verified === true,
    providerReachable: probe?.provider_reachable === true,
    phoneIdentityVerified: probe?.phone_number_identity_verified === true,
    failureCode: probe?.failure_code || null,
    checkedAt: probe?.checked_at || null,
    messageSendingPerformed: probe?.message_sending_performed === true,
  };
}

function probeFailureCopy(code) {
  if (code === 'authentication_or_permission') return 'Meta rejected the token or permissions. Check the access token and WhatsApp permissions in the deployment environment.';
  if (code === 'phone_number_not_found') return 'Meta could not verify the configured phone number ID.';
  if (code === 'provider_rejected') return 'Meta rejected the connectivity check. Review the provider configuration before sending any test message.';
  return 'CareOps could not safely verify the Meta connection. No WhatsApp message was sent.';
}

function StatusBadge({ ready, children }) {
  return (
    <Badge
      variant="outline"
      className={ready
        ? 'border-emerald-300 bg-emerald-50 text-emerald-800'
        : 'border-amber-300 bg-amber-50 text-amber-800'}
    >
      {ready ? <CheckCircle2 className="mr-1 h-3 w-3" /> : <AlertTriangle className="mr-1 h-3 w-3" />}
      {children}
    </Badge>
  );
}

function DomainReadinessCard({ title, summary, description }) {
  const conversationStartMapped = summary.configuredTemplateKeys.includes('conversation_start');
  return (
    <div className="rounded-lg border border-slate-200 bg-slate-50 p-3">
      <div className="flex flex-wrap items-center justify-between gap-2">
        <p className="text-xs font-semibold text-slate-900">{title}</p>
        <StatusBadge ready={conversationStartMapped}>
          {conversationStartMapped ? 'First-contact mapped' : 'First-contact not mapped'}
        </StatusBadge>
      </div>
      <p className="mt-2 text-xs text-slate-600">{description}</p>
      <p className="mt-2 text-xs text-slate-600">
        Approved mappings present: <strong>{summary.configuredCount}</strong>
      </p>
      {summary.templateEnvName ? (
        <p className="mt-1 text-xs text-slate-600">
          Mapping environment variable: <code className="rounded bg-white px-1 py-0.5">{summary.templateEnvName}</code>
        </p>
      ) : null}
    </div>
  );
}

export default function WhatsAppSetupReadinessPanel() {
  const { token } = useAuth();
  const headers = useMemo(() => ({ Authorization: `Bearer ${token}` }), [token]);
  const [setup, setSetup] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [probing, setProbing] = useState(false);
  const [probeResult, setProbeResult] = useState(null);

  useEffect(() => {
    let active = true;
    if (!token) {
      setLoading(false);
      return () => { active = false; };
    }

    setLoading(true);
    setError('');
    axios.get(`${API}/onboarding-communications/whatsapp/setup`, { headers })
      .then((response) => {
        if (!active) return;
        setSetup(response.data || null);
      })
      .catch((requestError) => {
        if (!active) return;
        console.warn('Could not load WhatsApp setup readiness', requestError);
        setError(requestError?.response?.data?.detail || 'WhatsApp setup status is unavailable');
      })
      .finally(() => {
        if (active) setLoading(false);
      });

    return () => { active = false; };
  }, [headers, token]);

  const summary = summarizeWhatsAppSetup(setup);
  const probe = summarizeWhatsAppProbe(probeResult);
  const liveVerified = probe.performed ? probe.verified : summary.liveVerified;

  const runLiveProbe = async () => {
    if (!token || !summary.liveProbeAvailable || probing) return;
    setProbing(true);
    setProbeResult(null);
    try {
      const response = await axios.post(
        `${API}/onboarding-communications/whatsapp/setup/probe`,
        {},
        { headers },
      );
      setProbeResult(response.data || null);
    } catch (requestError) {
      setProbeResult({
        live_probe_performed: false,
        live_connectivity_verified: false,
        failure_code: requestError?.response?.data?.detail || 'probe_request_failed',
        message_sending_performed: false,
      });
    } finally {
      setProbing(false);
    }
  };

  return (
    <div
      className="mt-4 rounded-xl border border-slate-200 bg-white p-4"
      data-testid="whatsapp-setup-readiness-panel"
    >
      <div className="flex flex-col gap-3 sm:flex-row sm:items-start sm:justify-between">
        <div>
          <p className="flex items-center gap-2 text-sm font-semibold text-slate-900">
            <MessageCircle className="h-4 w-4" />
            Meta WhatsApp setup readiness
          </p>
          <p className="mt-1 text-xs text-slate-600">
            Configuration presence is shown separately from live connectivity. Verify the connection here before any controlled test message is sent.
          </p>
        </div>
        <div className="flex flex-wrap gap-2">
          <StatusBadge ready={summary.manualSupportReady}>Human support</StatusBadge>
          <StatusBadge ready={summary.webhookReady}>Webhook</StatusBadge>
          <StatusBadge ready={liveVerified}>Live verified</StatusBadge>
        </div>
      </div>

      {loading ? (
        <p className="mt-3 text-xs text-slate-500">Checking secret-free WhatsApp configuration status…</p>
      ) : error ? (
        <p className="mt-3 text-xs text-red-700">{error}</p>
      ) : (
        <>
          {summary.missing.length > 0 ? (
            <div className="mt-3 rounded-lg border border-amber-200 bg-amber-50 p-3">
              <p className="text-xs font-semibold text-amber-900">Shared provider / webhook setup still required</p>
              <ul className="mt-2 space-y-1 text-xs text-amber-900">
                {summary.missing.map((item) => (
                  <li key={item.key}>
                    {item.label}
                    {item.env_name ? <code className="ml-1 rounded bg-white/70 px-1 py-0.5">{item.env_name}</code> : null}
                  </li>
                ))}
              </ul>
            </div>
          ) : (
            <div className="mt-3 rounded-lg border border-emerald-200 bg-emerald-50 p-3 text-xs text-emerald-900">
              <p className="font-semibold">Human WhatsApp support configuration is present.</p>
              <p className="mt-1">Personal replies work after the person messages Osabea. To start WhatsApp from a profile before they message first, CareOps uses one manually-triggered approved conversation opener.</p>
            </div>
          )}

          <div className="mt-3 grid gap-3 md:grid-cols-2" data-testid="whatsapp-domain-readiness">
            <DomainReadinessCard
              title="Recruitment WhatsApp"
              summary={summary.recruitment}
              description="Applicant history stays in Recruitment. The conversation_start mapping enables first contact; CareOps checks Meta's live approval status before showing the profile send button."
            />
            <DomainReadinessCard
              title="Staff WhatsApp"
              summary={summary.staff}
              description="Employee history stays in Staff Communications. The separate conversation_start mapping is checked against Meta approval before Staff can begin WhatsApp from a profile."
            />
          </div>

          <div className="mt-3 rounded-lg border border-slate-200 bg-slate-50 p-3">
            <div className="flex flex-col gap-3 sm:flex-row sm:items-center sm:justify-between">
              <div className="text-xs text-slate-700">
                <p className="font-semibold text-slate-900">Live Meta connectivity</p>
                <p className="mt-1">This read-only check verifies the configured token and phone-number identity. It does not send a WhatsApp message.</p>
              </div>
              <Button
                type="button"
                size="sm"
                variant="outline"
                onClick={runLiveProbe}
                disabled={!summary.liveProbeAvailable || probing}
                data-testid="whatsapp-live-probe-button"
              >
                {probing ? <Loader2 className="mr-2 h-4 w-4 animate-spin" /> : <ShieldCheck className="mr-2 h-4 w-4" />}
                Verify live connection
              </Button>
            </div>
            {!summary.liveProbeAvailable ? (
              <p className="mt-2 text-xs text-amber-800">Complete the shared Meta provider configuration and workspace allow-list before running this check.</p>
            ) : null}
            {probe.performed ? (
              <div
                className={`mt-3 rounded-md border p-2 text-xs ${
                  probe.verified
                    ? 'border-emerald-200 bg-emerald-50 text-emerald-900'
                    : 'border-amber-200 bg-amber-50 text-amber-900'
                }`}
                data-testid="whatsapp-live-probe-result"
              >
                <p className="font-semibold">{probe.verified ? 'Meta connection verified' : 'Meta connection not verified'}</p>
                <p className="mt-1">
                  {probe.verified
                    ? 'CareOps reached Meta and verified the configured phone-number identity. No message was sent.'
                    : probeFailureCopy(probe.failureCode)}
                </p>
                {probe.checkedAt ? <p className="mt-1 opacity-75">Checked: {new Date(probe.checkedAt).toLocaleString('en-GB')}</p> : null}
              </div>
            ) : probeResult ? (
              <div className="mt-3 rounded-md border border-red-200 bg-red-50 p-2 text-xs text-red-800">
                The live check could not start. No WhatsApp message was sent.
              </div>
            ) : null}
          </div>

          <div className="mt-3 flex items-start gap-2 rounded-lg border border-slate-200 bg-slate-50 p-3 text-xs text-slate-700">
            <ShieldCheck className="mt-0.5 h-4 w-4 shrink-0 text-slate-600" />
            <div>
              <p>
                Webhook callback path: <code className="rounded bg-white px-1 py-0.5">{summary.callbackPath}</code>
              </p>
              <p className="mt-1">
                Credential values and provider template names are never returned by this screen. Template mappings are optional for business-initiated messaging; human Recruitment support uses the applicant-initiated support window.
              </p>
            </div>
          </div>
        </>
      )}
    </div>
  );
}
